export type NormalizedUserRole =
  | 'motorista'
  | 'admin'
  | 'desenvolvedor'
  | 'gestao'
  | 'desconhecido';

const normalizeRawUserType = (userType?: string | null): string => {
  if (!userType) return '';
  return userType.trim().toLowerCase();
};

export const normalizeUserType = (userType?: string | null): NormalizedUserRole => {
  const normalized = normalizeRawUserType(userType);

  if (!normalized) return 'desconhecido';
  if (normalized === 'usuário' || normalized === 'usuario' || normalized === 'motorista') {
    return 'motorista';
  }
  if (normalized === 'admin') {
    return 'admin';
  }
  if (normalized === 'desenvolvedor' || normalized === 'developer') {
    return 'desenvolvedor';
  }
  if (normalized === 'gestao') {
    return 'gestao';
  }

  return 'desconhecido';
};

export const isMotoristaUserType = (userType?: string | null): boolean =>
  normalizeUserType(userType) === 'motorista';

export const isAdminUserType = (userType?: string | null): boolean =>
  normalizeUserType(userType) === 'admin';

export const isDeveloperUserType = (userType?: string | null): boolean =>
  normalizeUserType(userType) === 'desenvolvedor';

export const canAccessAdminDashboard = (userType?: string | null): boolean =>
  isAdminUserType(userType) || isDeveloperUserType(userType);

export const canAccessChecklistDashboard = (userType?: string | null): boolean =>
  isMotoristaUserType(userType) || isAdminUserType(userType) || isDeveloperUserType(userType);

export const canManageCompanyUsers = (userType?: string | null): boolean =>
  isAdminUserType(userType);

export const canAccessChecklistSettings = (userType?: string | null): boolean =>
  isAdminUserType(userType) || isDeveloperUserType(userType);

export const canAccessCompanySettings = (userType?: string | null): boolean =>
  isAdminUserType(userType) || isDeveloperUserType(userType);

export const canAccessFinancialSettings = (userType?: string | null): boolean =>
  isAdminUserType(userType) || isDeveloperUserType(userType);

export const getUserTypeLabel = (userType?: string | null): string => {
  const normalized = normalizeUserType(userType);

  if (normalized === 'admin') return 'Administrador';
  if (normalized === 'motorista') return 'Motorista';
  if (normalized === 'desenvolvedor') return 'Desenvolvedor';
  if (normalized === 'gestao') return 'Gestor (legado)';
  return 'Desconhecido';
};

export const getCompanyManagedUserTypeOptions = () => [
  { label: 'Motorista', value: 'motorista' },
  { label: 'Administrador', value: 'admin' },
];

type ChecklistPermissionParams = {
  userType?: string | null;
  checklistUsuarioId?: string | null;
  checklistEmpresaId?: string | null;
  currentUserId?: string | null;
  currentEmpresaId?: string | null;
};

export const canViewChecklistForRole = ({
  userType,
  checklistUsuarioId,
  checklistEmpresaId,
  currentUserId,
  currentEmpresaId,
}: ChecklistPermissionParams): boolean => {
  if (isDeveloperUserType(userType)) {
    return true;
  }

  if (isAdminUserType(userType)) {
    if (!currentEmpresaId || !checklistEmpresaId) {
      return false;
    }
    return checklistEmpresaId === currentEmpresaId;
  }

  if (!isMotoristaUserType(userType)) {
    return false;
  }

  if (!currentUserId || !checklistUsuarioId || !currentEmpresaId || !checklistEmpresaId) {
    return false;
  }

  return checklistUsuarioId === currentUserId && checklistEmpresaId === currentEmpresaId;
};

export const canEditChecklistForRole = (params: ChecklistPermissionParams): boolean =>
  canViewChecklistForRole(params);

export const canDeleteChecklistForRole = (
  params: ChecklistPermissionParams & { checklistRetiredAt?: string | null }
): boolean =>
  params.checklistRetiredAt == null && canEditChecklistForRole(params);
